import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const SESSION_KEY = "guest_booking_access"

// Actions
const verifyRequested = createAction("guestAccess/verifyRequested")
const verifySuccess = createAction("guestAccess/verifySuccess")
const verifyFailed = createAction("guestAccess/verifyFailed")
const cleared = createAction("guestAccess/cleared")

// Which identity-verification method applies to this booking's client:
// "email" (default, existing flow) or "phone" (client has no real email on file).
const methodRequested = createAction("guestAccess/methodRequested")
const methodReceived = createAction("guestAccess/methodReceived")
const methodFailed = createAction("guestAccess/methodFailed")

// Phone flow, step 1: send an OTP by SMS to the phone number on file.
const otpSendRequested = createAction("guestAccess/otpSendRequested")
const otpSendSuccess = createAction("guestAccess/otpSendSuccess")
const otpSendFailed = createAction("guestAccess/otpSendFailed")

const getInitialState = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...LOADING_INITIAL_STATE,
        authorized: parsed.authorized || false,
        bookingUuid: parsed.bookingUuid || null,
        method: null,
        otpSent: false,
        error: null
      }
    }
  } catch (e) {
    // ignore malformed session data
  }
  return {
    ...LOADING_INITIAL_STATE,
    authorized: false,
    bookingUuid: null,
    method: null,
    otpSent: false,
    error: null
  }
}

const guestAccessReducer = createReducer(getInitialState(), {
  [methodRequested.type]: (state) => {
    state.error = null
  },
  [methodReceived.type]: (state, action) => {
    state.method = action.payload.method
  },
  [methodFailed.type]: (state, action) => {
    state.error =
      action.payload?.data?.message || action.payload?.message || "Unable to load this booking. Please try again."
  },
  [otpSendRequested.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [otpSendSuccess.type]: (state) => {
    setReceived(state)
    state.otpSent = true
  },
  [otpSendFailed.type]: (state, action) => {
    setFailed(state)
    state.otpSent = false
    state.error =
      action.payload?.data?.message || action.payload?.message || "Phone number does not match. Please try again."
  },
  [verifyRequested.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [verifySuccess.type]: (state, action) => {
    setReceived(state)
    state.authorized = true
    state.bookingUuid = action.payload.bookingUuid
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ authorized: true, bookingUuid: action.payload.bookingUuid })
    )
  },
  [verifyFailed.type]: (state, action) => {
    setFailed(state)
    state.authorized = false
    state.error =
      action.payload?.data?.message ||
      action.payload?.message ||
      "Verification failed. Please try again."
  },
  [cleared.type]: () => {
    sessionStorage.removeItem(SESSION_KEY)
    localStorage.removeItem("guest_token")
    return { ...LOADING_INITIAL_STATE, authorized: false, bookingUuid: null, method: null, otpSent: false, error: null }
  }
})

export default guestAccessReducer

// Action Creators

/**
 * Verifies that the provided email matches the primary guest for the booking.
 * On success, the backend returns a guest_token which is stored automatically
 * by the api helper, enabling subsequent authenticated API calls.
 */
export const verifyGuestEmail = (uuid, email) => async (dispatch) => {
  dispatch(verifyRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/authorize_client`,
        method: "get",
        params: { email },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    // Store tokens so authHeader() includes them in all subsequent API calls
    if (result?.access_token) {
      localStorage.setItem("guest_token", result.access_token)
    }
    if (result?.refresh_token) {
      localStorage.setItem("refresh_token", result.refresh_token)
    }
    dispatch(verifySuccess({ bookingUuid: uuid }))
    // Dispatch custom event to notify components
    window.dispatchEvent(new Event('guestAuthSuccess'))
    return result
  } catch (error) {
    dispatch(verifyFailed(error))
    throw error
  }
}

/**
 * Determines whether this booking's client should be verified by email or by
 * phone (clients imported/created without a real email are phone-only).
 */
export const getGuestVerificationMethod = (uuid) => async (dispatch) => {
  dispatch(methodRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/verification_method`,
        method: "get",
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    dispatch(methodReceived({ method: result?.method || "email" }))
    return result
  } catch (error) {
    dispatch(methodFailed(error))
    throw error
  }
}

/**
 * Phone flow, step 1: confirms the entered phone number matches the booking's
 * client and triggers an SMS OTP.
 */
export const sendGuestBookingOtp = (uuid, phoneNumber) => async (dispatch) => {
  dispatch(otpSendRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/send_booking_otp`,
        method: "post",
        data: { phone_number: phoneNumber },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    dispatch(otpSendSuccess())
    return result
  } catch (error) {
    dispatch(otpSendFailed(error))
    throw error
  }
}

/**
 * Phone flow, step 2: verifies the SMS OTP and grants access, mirroring
 * verifyGuestEmail's token handling below.
 */
export const verifyGuestBookingOtp = (uuid, otp) => async (dispatch) => {
  dispatch(verifyRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/verify_booking_otp`,
        method: "post",
        data: { otp },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    if (result?.access_token) {
      localStorage.setItem("guest_token", result.access_token)
    }
    if (result?.refresh_token) {
      localStorage.setItem("refresh_token", result.refresh_token)
    }
    dispatch(verifySuccess({ bookingUuid: uuid }))
    window.dispatchEvent(new Event('guestAuthSuccess'))
    return result
  } catch (error) {
    dispatch(verifyFailed(error))
    throw error
  }
}

export const clearGuestAccess = () => cleared()
